/**
 * Sales Repository
 * 영업 관리 데이터 액세스 계층
 */

import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import {
  SalesLead,
  SalesActivity,
  SalesStatistics,
  CreateLeadRequest,
  UpdateLeadRequest,
  UpdateStatusRequest,
  AssignLeadRequest,
  CompleteContractRequest,
  AddActivityRequest,
} from '../models/SalesLead';
import { ServiceType } from '../models';

export class SalesRepository {
  private baseUrl = `${API_BASE_URL}/api/admin/sales`;

  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  // ============================================
  // 목록 조회
  // ============================================

  async getAllLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(`${this.baseUrl}/`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getNewInquiryLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(`${this.baseUrl}/new-inquiry`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getConsultingLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(`${this.baseUrl}/consulting`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getContractedLeads(serviceType?: ServiceType): Promise<SalesLead[]> {
    const params = serviceType ? { service_type: serviceType } : {};
    const response = await axios.get(`${this.baseUrl}/contracted`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return response.data;
  }

  async getMyLeads(): Promise<SalesLead[]> {
    const response = await axios.get(`${this.baseUrl}/my-leads`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async searchLeads(query: string): Promise<SalesLead[]> {
    const response = await axios.get(`${this.baseUrl}/search`, {
      headers: this.getAuthHeaders(),
      params: { q: query },
    });
    return response.data;
  }

  async getStatistics(): Promise<SalesStatistics> {
    const response = await axios.get(`${this.baseUrl}/statistics`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // ============================================
  // 상세 조회 및 생성
  // ============================================

  async getLeadById(id: string): Promise<SalesLead> {
    const response = await axios.get(`${this.baseUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createLead(request: CreateLeadRequest): Promise<SalesLead> {
    const response = await axios.post(this.baseUrl, request, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async createLeadFromContact(contactId: string): Promise<SalesLead> {
    const response = await axios.post(
      `${this.baseUrl}/from-contact/${contactId}`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // ============================================
  // 수정
  // ============================================

  async updateLead(id: string, request: UpdateLeadRequest): Promise<SalesLead> {
    const response = await axios.put(`${this.baseUrl}/${id}`, request, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async updateStatus(id: string, request: UpdateStatusRequest): Promise<SalesLead> {
    const response = await axios.put(`${this.baseUrl}/${id}/status`, request, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async assignLead(id: string, request: AssignLeadRequest): Promise<SalesLead> {
    const response = await axios.post(`${this.baseUrl}/${id}/assign`, request, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async completeContract(id: string, request: CompleteContractRequest): Promise<SalesLead> {
    const response = await axios.post(`${this.baseUrl}/${id}/complete-contract`, request, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  // ============================================
  // 활동
  // ============================================

  async getActivities(leadId: string): Promise<SalesActivity[]> {
    const response = await axios.get(`${this.baseUrl}/${leadId}/activities`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async addActivity(leadId: string, request: AddActivityRequest): Promise<SalesActivity> {
    const response = await axios.post(`${this.baseUrl}/${leadId}/activities`, request, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
}
